import type { ChatChannel } from '@shefira/shared';

/** Where a line goes when the player presses Enter. */
export interface ChatTarget {
  channel: ChatChannel;
  /** The addressee of a private line. */
  to?: string;
}

/** Private correspondents offered in the channel list. */
export const RECENT_CORRESPONDENTS = 3;

/** The public channels, in the order Tab walks them. */
export const PUBLIC_CHANNELS: readonly Exclude<ChatChannel, 'private'>[] = [
  'zone',
  'general',
  'party',
  'guild',
];

/** `name` first, without its older copy (names compare without case), at most RECENT_CORRESPONDENTS. */
export function rememberCorrespondent(recent: readonly string[], name: string): string[] {
  const key = name.toLowerCase();
  return [name, ...recent.filter((each) => each.toLowerCase() !== key)].slice(
    0,
    RECENT_CORRESPONDENTS,
  );
}

/** Every target the channel button lists: the public channels, then one private target per recent correspondent. */
export function channelTargets(recent: readonly string[]): ChatTarget[] {
  return [
    ...PUBLIC_CHANNELS.map((channel): ChatTarget => ({ channel })),
    ...recent.map((to): ChatTarget => ({ channel: 'private', to })),
  ];
}

/** Same target: same channel and, for a private one, same name without case. */
export function sameTarget(a: ChatTarget, b: ChatTarget): boolean {
  if (a.channel !== b.channel) return false;
  return a.channel !== 'private' || (a.to ?? '').toLowerCase() === (b.to ?? '').toLowerCase();
}

/**
 * The target `step` places after `current` (1: Tab, -1: Shift+Tab), wrapping
 * around. A private target that is not in the list (a name typed by /m) counts
 * as standing just before the first correspondent.
 */
export function stepTarget(
  current: ChatTarget,
  recent: readonly string[],
  step: 1 | -1,
): ChatTarget {
  const targets = channelTargets(recent);
  const found = targets.findIndex((each) => sameTarget(each, current));
  const from = found >= 0 ? found : PUBLIC_CHANNELS.length - 0.5;
  const index = Math.ceil(from + step * (found >= 0 ? 1 : 0.5));
  const wrapped = ((index % targets.length) + targets.length) % targets.length;
  return targets[wrapped] ?? { channel: 'zone' };
}
